import { Component, ViewChild, AfterViewInit } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from "../../core/components/header/header.component";
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { LoaderService } from '../../core/services/loader.service';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { ToastComponent } from '../../core/components/toast/toast.component';
import { HttpClientService } from '../../core/services/http-client.service';
import { EnumStatusResponse } from '../../core/enum/EnumHttpResponse';

@Component({
  selector: 'app-autenticacao',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, CommonModule, HeaderComponent, ToastComponent],
  templateUrl: './autenticacao-aberta.component.html',
  styleUrl: './autenticacao-aberta.component.scss'
})
export class AutenticacaoAbertaComponent implements AfterViewInit {
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;

  mostrarHeader: boolean = false;
  selectedOption: 'email' | 'sms' | null = null;
  selectedContact: string = ''; // Contato específico selecionado (email ou telefone)
  telaCodigo: boolean = false;

  contato: string = '';
  timer: number = 60;
  timerInterval: any;
  podeReenviar: boolean = false;
  codigo: string[] = ['', '', '', '', '', ''];

  // Arrays de contatos
  emails: string[] = [];
  telefones: string[] = [];

  // Valores mascarados para exibição
  emailsMascarados: string[] = [];
  telefonesMascarados: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private loaderService: LoaderService,
    private localStorageService: LocalStorageService,
    private toastService: ToastService,
    private httpClientService: HttpClientService
  ) {}

  ngAfterViewInit() {
    // Configura o componente de toast no serviço
    if (this.toastComponent) {
      this.toastService.setToastComponent(this.toastComponent);
    }
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const mostrarHeaderParam = params['MostrarHeader'];

      if (mostrarHeaderParam) {
        this.mostrarHeader = mostrarHeaderParam === 'true';
      } else if (typeof window !== 'undefined') {
        const mostrarHeaderStorage = this.localStorageService.get('MostrarHeader'); // PADRONIZADO
        this.mostrarHeader = mostrarHeaderStorage === 'true';
      }

      if (typeof window !== 'undefined') {
        this.localStorageService.add('MostrarHeader', this.mostrarHeader ? 'true' : 'false');
      }
    });

    if (typeof window !== 'undefined') {
      // Carrega arrays de emails e telefones do localStorage
      const emailsJson = this.localStorageService.get('emails');
      const telefonesJson = this.localStorageService.get('telefones');

      if (emailsJson) {
        try {
          const emailsRaw = JSON.parse(emailsJson);
          // Filtra emails vazios, nulos ou que contenham apenas espaços
          this.emails = emailsRaw.filter((email: string) =>
            email && email.trim() !== '' && email.includes('@')
          );
          this.emailsMascarados = this.emails.map(email => this.mascararEmail(email));
        } catch (e) {
          console.error('Erro ao fazer parse dos emails:', e);
        }
      }

      if (telefonesJson) {
        try {
          const telefonesRaw = JSON.parse(telefonesJson);
          // Filtra telefones inválidos: "0", vazios, nulos ou que não tenham pelo menos 10 dígitos
          this.telefones = telefonesRaw.filter((telefone: string) => {
            if (!telefone || telefone.trim() === '') return false;
            const numeros = telefone.replace(/\D/g, '');
            // Remove se for "0" ou tiver menos de 10 dígitos
            return numeros !== '0' && numeros.length >= 10;
          });
          this.telefonesMascarados = this.telefones.map(telefone => this.mascararTelefone(telefone));
        } catch (e) {
          console.error('Erro ao fazer parse dos telefones:', e);
        }
      }
    }
  }

  /**
   * Mascara o e-mail mostrando apenas os 3 primeiros caracteres e tudo após o @
   * Exemplo: fernando.piccoli04@gmail.com → fer*******@gmail.com
   */
  private mascararEmail(email: string): string {
    if (!email || !email.includes('@')) {
      return email;
    }

    const [usuario, dominio] = email.split('@');

    if (usuario.length <= 3) {
      return `${usuario}***@${dominio}`;
    }

    const prefixo = usuario.substring(0, 3);
    const asteriscos = '*'.repeat(Math.max(usuario.length - 3, 3));

    return `${prefixo}${asteriscos}@${dominio}`;
  }

  /**
   * Mascara o telefone mostrando apenas DDD e últimos 4 dígitos
   * Exemplo: 11989806502 → (11)98***-6502
   * Remove prefixo "00" se presente: 0011983519015 → (11)98***-9015
   */
  private mascararTelefone(telefone: string): string {
    if (!telefone) {
      return telefone;
    }

    // Remove caracteres não numéricos
    let numeros = telefone.replace(/\D/g, '');

    // Remove prefixo "00" se presente
    if (numeros.startsWith('00')) {
      numeros = numeros.substring(2);
    }

    if (numeros.length < 10) {
      return telefone; // Telefone inválido, retorna como está
    }

    // Extrai partes do telefone
    const ddd = numeros.substring(0, 2);
    const primeirosDigitos = numeros.substring(2, numeros.length - 4);
    const ultimosDigitos = numeros.substring(numeros.length - 4);

    // Cria máscara
    const asteriscos = '*'.repeat(Math.min(primeirosDigitos.length, 3));

    return `(${ddd})${primeirosDigitos.substring(0, 2)}${asteriscos}-${ultimosDigitos}`;
  }

  enviarCodigo() {
    if (!this.selectedContact) {
      return;
    }

    this.loaderService.showLoader();

    const cpf = this.localStorageService.get('cpf');

    // Remove espaços e caracteres especiais do telefone antes de enviar
    const contatoLimpo = this.selectedOption === 'sms'
      ? this.selectedContact.replace(/\D/g, '') // Remove tudo que não é número
      : this.selectedContact; // Email mantém como está

    const body = {
      receptor: contatoLimpo, // Usa o contato limpo (sem espaços/caracteres especiais)
      cpf: cpf
    };

    const headers = new HttpHeaders({
      "Authorization": `Bearer ${this.localStorageService.get('bearerToken')}`,
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.notificationsubscriptionkey}`
    });

    const url = this.selectedOption === 'sms'
      ? `${environment.baseUrl}/apis/api-notificacao/v1/notificacao/confirmacao-sms`
      : `${environment.baseUrl}/apis/api-notificacao/v1/notificacao/confirmacao-email`;

    this.http.post(url, body, { headers }).subscribe(
      response => {
        this.contato = this.selectedOption === 'sms'
          ? this.mascararTelefone(this.selectedContact)
          : this.mascararEmail(this.selectedContact);
        this.telaCodigo = true;
        this.loaderService.hideLoader();
        this.iniciarTimer();
      },
      error => {
        console.error('Erro ao enviar notificação:', error);
        this.loaderService.hideLoader();
      }
    );
  }

  iniciarTimer() {
    this.timer = 60;
    this.podeReenviar = false;
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      if (this.timer > 0) {
        this.timer--;
      } else {
        this.podeReenviar = true;
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  reenviarCodigo() {
    // Volta para a tela de seleção de método
    this.telaCodigo = false;

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.codigo = ['', '', '', '', '', ''];
    this.podeReenviar = false;
    this.timer = 60;

    // Limpa a seleção anterior para forçar o usuário a escolher novamente
    this.selectedOption = null;
    this.selectedContact = '';
  }

  onInput(index: number, event: any) {
    const input = event.target as HTMLInputElement;
    const valor = input.value.replace(/[^0-9]/g, '');

    if (valor) {
      this.codigo[index] = valor;
      // Move para o próximo
      if (index < 5) {
        const proximo = document.getElementById(`codigo-${index + 1}`) as HTMLInputElement;
        if (proximo) proximo.focus();
      }
    } else {
      this.codigo[index] = '';
    }
  }

  onKeydown(index: number, event: KeyboardEvent) {
    if (event.key === 'Backspace') {
      const input = event.target as HTMLInputElement;
      if (!input.value && index > 0) {
        const anterior = document.getElementById(`codigo-${index - 1}`) as HTMLInputElement;
        if (anterior) anterior.focus();
      }
    }
  }

  validarCodigo() {
    const codigoFinal = this.codigo.join('');

    this.loaderService.showLoader();

    const cpf = localStorage.getItem('cpf');
    const body = {
      cpf: cpf,
      verificationCode: codigoFinal,
      receptorType: this.selectedOption === 'sms' ? 1 : 2
    };

    this.httpClientService.validaToken(JSON.stringify(body))
      .then((response: HttpResponse<any>) => {
        if (response.status === EnumStatusResponse.Success && response.body && response.body.validated) {
          // Mostra mensagem de sucesso
          this.toastService.show('Token validado! Redirecionando...', 'success', 3000);

          // marca sessão válida
          this.localStorageService.add('isValidSession', 'true');

          // Aguarda 2 segundos antes de redirecionar para o usuário ver a mensagem
          setTimeout(() => {
            this.loaderService.hideLoader();
            // Redireciona para /home
            this.router.navigate(['/home'], {
              queryParams: { MostrarHeader: this.mostrarHeader ? 'true' : 'false' }
            });
          }, 2000);
        } else {
          this.toastService.show('Token inválido: ' + codigoFinal, 'error', 5000);
          this.loaderService.hideLoader();
        }
      })
      .catch(error => {
        this.loaderService.hideLoader();
        console.error('Erro na validação do token:', error);
        this.toastService.show('Token inválido: ' + codigoFinal, 'error', 5000);
      });
  }

  async sessionLogin(cpf: number): Promise<HttpResponse<any>> {
    const url = `${environment.baseUrl}/apis/api-adesao-cadastro/v1/Sessao/login`;
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Ocp-Apim-Subscription-Key": `${environment.ocpDadosCadastrais}`
    });


    return await lastValueFrom(this.http.post<any>(url, { cpf: cpf }, { headers, observe: 'response' }))
      .catch(error => {
        return new HttpResponse({ body: error });
      });
  }

  sessionLoginHandler(cpf: number) {
    this.loaderService.showLoader();

    this.sessionLogin(cpf)
      .then((response: HttpResponse<any>) => {
        if (response.status === 200) {
          this.localStorageService.add("bearerToken", response.body.bearerToken);
          this.localStorageService.add("refreshToken", response.body.refreshToken);
          this.loaderService.hideLoader();
        }
      })
      .catch(error => {
        console.error("Erro ao realizar login de sessão:", error);
        this.loaderService.hideLoader();
      });
  }

  /**
   * Atualiza a seleção do tipo (email ou sms) e do contato específico
   */
  onSelectContact(type: 'email' | 'sms', contact: string) {
    this.selectedOption = type;
    this.selectedContact = contact;
  }
}
